// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import { api } from "./index";
import {
  BatteryLevel,
  DataUsageLevel,
  DeviceGroupID,
  DeviceStatus,
  DeviceType,
  IDevice,
  ILite360Group,
  OnlineStatus,
  TaskType
} from "../constants/device";
import { IPageRequest, IPageResponse } from "constants/page";
import { ICity } from "../models/city";

type deviceIDResult = {
  deviceID: string
}
type DeviceDriverPair = {
  deviceID: string
  driverID: string
}

export interface IAddDevicesRes {
  emptyIDCount: number
  existDevices: deviceIDResult[]
  insertFailedDevices: deviceIDResult[]
  insertSuccessDevices: deviceIDResult[]
  existDriversDevices?: DeviceDriverPair[]
  existDrivers?: string[]
  emptyCityIDDevices?: deviceIDResult[]
  duplicateDriverDevicesInReq?: DeviceDriverPair[]
  duplicateDevicesInReq?: deviceIDResult[]
}

export function fetchDevicesInGroup(groupId:DeviceGroupID):Promise<ILite360Group>{
  return api.get(`/devices/group/${groupId}`)
}

export function addDevices (devices: any[]): Promise<{ result: IAddDevicesRes }> {
  return api.post('/devices', { devices })
}

export function fetchCityList (): Promise<{ cities: ICity[] }> {
  return new Promise((resolve, reject) => {
    api
      .get('/cities')
      .then((res: any) => {
        const result = res.cities
          .map(c => ({
            id: c.id,
            name: c.name,
            enabled: c.enabled,
            longitude: c.lon,
            latitude: c.lat,
            timezone: c.time_zone,
            country: c.country,
            continent: c.continent
          }))
          .filter(x => x.enabled)
        resolve({ cities: result })
      })
      .catch(err => reject(err))
  })
}

export function getFirmwareVersions (): Promise<string[]> {
  return api.get('/firmware-versions')
}

export interface ILatestFirmwareVersion {
  standard: string
  recovery: string
}
export function getLatestFirmwareVersion (): Promise<ILatestFirmwareVersion> {
  return api.get('/latest-firmware-versions')
}

export interface IDeviceListQuery extends IPageRequest {
  testMode?: boolean
  batteryLevel?: BatteryLevel[]
  firmwareVersion?: string[]
  taskType?: TaskType[]
  deviceType?: DeviceType[]
  onlineStatus?: OnlineStatus[]
  status?: DeviceStatus[]
  quotaUsageLevel?: DataUsageLevel[]
  keyword?: string
  bbox?: number[]
  cityID?: number
  driverID?: string
  groupID?: DeviceGroupID
}
export type IDeviceList = Omit<IDevice, 'lon' | 'lat' | 'settingID' | 'driverID'>[]
export type IDeviceListResponse = {
  metadata: IPageResponse
  results: IDeviceList
}

export function getDeviceList(query: IDeviceListQuery): Promise<IDeviceListResponse> {
  /*
  if (query.deviceType) {
    let deviceType = [...query.deviceType];
    let index = deviceType.indexOf(DeviceType.Lite360Camera);
    if (index > -1) {
      deviceType.splice(index, 1);
      deviceType.push(DeviceType.Lite360Master, DeviceType.Lite360Slave);
      query.deviceType = deviceType;
    }
  }
  */
  return api.get("/devices", { params: query });
}

export interface IDeviceTraceRequestParams {
  deviceId: string
  startTime: any
  endTime: any
}
export interface DeviceTrace {
  lat: number
  lon: number
  timestamp: number
}
export interface IDeviceTraceResponse {
  matchStatus: number
  trace: DeviceTrace[]
}

export function fetchDeviceTrace (params: IDeviceTraceRequestParams): Promise<IDeviceTraceResponse> {
  return api.get(`/device/${params.deviceId}/traces?startTime=${params.startTime}&endTime=${params.endTime}`)
}

export interface IDeviceByIdResponse {
  deviceID: string
  cityID: number
  longitude: number
  latitude: number
}

export interface IRoadNameResponse {
  roadName: string
}

export function fetchDeviceById (deviceId: string): Promise<IDeviceByIdResponse> {
  return api.get(`/devices/${deviceId}`)
}
export function getDeviceById (deviceID: string): Promise<IDevice> {
  return api.get(`/devices/${deviceID}`)
}

export function getRoadName (deviceID: string): Promise<IRoadNameResponse> {
  return api.get(`/devices/roadname/${deviceID}`)
}

export interface IDeviceUdpateParam {
  driverID?: string
  taskType?: TaskType
  deviceType?: DeviceType
  testMode?: boolean
  cityID?: number
  masterDeviceDirection?:number
}
export function updateDevice (deviceID: string, values: IDeviceUdpateParam): Promise<IDevice> {
  return api.patch(`/devices/${deviceID}`, values)
}

export function updateDeviceQuota (deviceID: string) {
  return api.put(`/devices/${deviceID}/quota`)
}

export interface IFuzzySearchDeviceIdParam {
  keyword: string
  limit?: number
  status: DeviceStatus
  onlineStatus: OnlineStatus
  cityID: number
}

export function fuzzySearchDeviceId (params: IFuzzySearchDeviceIdParam): Promise<string[]> {
  if (params.limit === undefined) {
    params.limit = 10
  }
  return api.get('/device-search', { params })
}

// map debugging
export interface IFetchDeviceIdParam {
  sequenceIDs: any
}

export function fetchDeviceIDs (params: IFetchDeviceIdParam): Promise<string[]> {
  return api.get('/device-search', { params })
}

export interface IDeviceResponse {
  results: DeviceStatusesResponse[]
  metadata: any
}
export interface IDeviceStatusesRequestParams {
  deviceId: string
  startTime: any
  endTime: any
}
export interface DeviceStatuses {
  lat: number
  lon: number
  report_time: number
}
export interface DeviceStatusesResponse {
  deviceStatuses: DeviceStatuses[]
  metadata: any
}

export function fetchDeviceStatuses (params: IDeviceStatusesRequestParams): Promise<IDeviceResponse> {
  return api.get(`/v1/device/${params.deviceId}/statuses?startTime=${params.startTime}&endTime=${params.endTime}`)
}